import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useSimpleOrdersForm from "./useSimpleOrdersForm";
import { getHyphenedDate } from "../../../../utils/utils";
import { postSimpleOrderEntitySearchInitialOrderAPI, postSimpleOrderEntitySearchOrderAPI, postSimpleOrderEntityUpdateOrderAPI } from "../../../../http/api/SimpleOrderEntityAPI";
import { getDisplaySize } from "../../../../utils/Items";
import { useAddProductDialogs } from "./useAddProductDialogs";
import { ScreenStatusContext } from "../../../../context/ScreenStatusContext";
import { stripTrailingZeros } from "../../../../utils/decimal";
import { flushSync } from "react-dom";



export const ORDER_TABLE_ROW_COUNT_PER_PAGE = 20;

/**
 * 注文単入力画面ステート管理fook
 */
const useSimpleOrderEntry = () =>  {
  const [shippingDate, setShippingDate] = useState(null);
  const [isShippingDateUndecided, setShippingDateUndecided] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [shops, setShops] = useState([]);
  
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState(null);

  const [isPriceUndecided, setPriceUndecided] = useState(false);
  const ordersForm = useSimpleOrdersForm();
  
  const [page, setPage] = useState(1);  
  const [tabPos, setTabPos] = useState(1);

  const addProductDialogs = useAddProductDialogs();

  const {isChanged, setChanged} = useContext(ScreenStatusContext);

  // 初期処理
  useEffect(() => {
    (async () => {
      searchInitialOrders();
    })()
      
  } ,[]);

  /**
   * 出荷日を変更する
   * @param shippingDateInput 出荷日(入力値)
   */
  const changeShippingDate = async (shippingDateInput) => {
    setShippingDate(shippingDateInput);

    if (isChanged && !await submitWhenConfirmed()) {
      setShippingDate(shippingDate);
    }

    await searchOrders(shippingDateInput, isShippingDateUndecided, selectedShopId);
  }

  /**
   * 出荷日未定チェックを変更する
   * @param isShippingDateUndecidedInput 出荷日未定チェック(入力値)
   */
  const changeShippingDateUndecided = async (isShippingDateUndecidedInput) => {
    setShippingDateUndecided(isShippingDateUndecidedInput);

    if (isChanged && !await submitWhenConfirmed()) {
      setShippingDateUndecided(isShippingDateUndecided);
      return;
    }

    await searchOrders(shippingDate, isShippingDateUndecidedInput, selectedShopId);
  }

  /**
   * 取引先を変更する
   * @param customerIdInput 取引先ID(入力値)
   */
  const changeCustomerSelect = async(customerIdInput) => {

    if (isChanged && !await submitWhenConfirmed()) {
      setSelectedCustomerId(selectedCustomerId);
      setSelectedShopId(selectedShopId);
      return;
    }
    
    const firstShop = shops.find((shop) => shop.customerId === customerIdInput);
    const newShopId = firstShop?.shopId || null;

    searchOrders(shippingDate, isShippingDateUndecided, newShopId);
    setSelectedCustomerId(customerIdInput);
    setSelectedShopId(newShopId); 
  }

  /**
   * 店舗を変更する
   * @param shopIdInput 店舗ID(入力値)
   */
  const changeShopSelect = async (shopIdInput) => {
    if (isChanged && !await submitWhenConfirmed()) {
      setSelectedCustomerId(selectedCustomerId);
      setSelectedShopId(selectedShopId);
      return;
    }

    searchOrders(shippingDate, isShippingDateUndecided, shopIdInput);
    setSelectedShopId(shopIdInput);
  }

  const changePriceUndecided = (isPriceUndecided) => {
    setPriceUndecided(isPriceUndecided);
  }

  const searchOrders = async (shippingDate, isShippingDateUndecided, shopId) => {

    if (!shopId) {
      ordersForm.setOrders([]);
      return;
    }
    
    const orderResponse = await postSimpleOrderEntitySearchOrderAPI({
      shippingDate: isShippingDateUndecided ? null : shippingDate,
      shopId
    });

    const orderDetails = orderResponse.orderDetails;

    ordersForm.setOrders(
      orderDetails.map((od, orderIndex)=> ({
        ...od,
        orderIndex,
        orderQuantity: od.orderQuantity ? stripTrailingZeros(od.orderQuantity): "",
        isError: false
      }))
    );

    setChanged(false);
  }

  const searchInitialOrders = async () => {
    const orderResponse = await postSimpleOrderEntitySearchInitialOrderAPI({});

    const {customers, shops, shippingDate} = orderResponse.simpleOrderInfo; 

    setCustomers(customers);
    setShops(shops);
    setShippingDate(shippingDate);

    setChanged(false);
  }

  const addProduct = useCallback(async () => {
    await addProductDialogs.openAddProductDialog((product) => {
      ordersForm.addOrder(product, isShippingDateUndecided ? null : shippingDate);
      changePage(1);
    });
  }, [ordersForm.addOrder, shippingDate, isShippingDateUndecided]);

  const submitOrders = async () => {
    try {
      await postSimpleOrderEntityUpdateOrderAPI({
        shippingDate,
        shopId: selectedShopId,
        orderDetails: ordersForm.orders
      });
      
      alert("注文を更新しました。")
      await searchOrders(shippingDate, isShippingDateUndecided, selectedShopId);
    } catch(e) {
      alert("注文の登録に失敗しました。");
    }
  } 
  
  const changePage = (page) => {
    flushSync(()=> {
      setPage(page);
    });
  }

  const changeTab = (tabPos) => {
    setTabPos(tabPos);
    setPage(1);
  }


  /** エラーメッセージ */
  const errorMessages = useMemo(() => {
  
    const messages = [];

    if (ordersForm.hasError()) {
      messages.push("入力にエラーがあります。");
    }

    return messages;
    
  }, [ordersForm]);

  /* 検索フィルター後の注文一覧 */
  const filteredOrders= ordersForm.orders
    .filter((order) => 
      (!isPriceUndecided || !order.orderPrice)
        && order.orderTypeId === tabPos
    );
  
  /**
   * 確定確認をユーザに行い、OKを押したら更新する
   */
  const submitWhenConfirmed = async () => {
    
    if (window.confirm("注文情報の入力が変更されています。確定しますか?")) {
      await postSimpleOrderEntityUpdateOrderAPI({
        shippingDate,
        shopId: selectedShopId,
        orderDetails: ordersForm.orders
      });
      return true;
    }
    
    return false;
  };

  return {
    shippingDate,
    isShippingDateUndecided,
    customers,
    shops,
    selectedCustomerId,
    selectedShopId,
    isPriceUndecided,
    ordersForm,
    pageOrdersForm : {
      ...ordersForm,
      orders : 
        filteredOrders
          .slice(
            ORDER_TABLE_ROW_COUNT_PER_PAGE * (page - 1),
            ORDER_TABLE_ROW_COUNT_PER_PAGE * page
          ).map((order, index, arr) => 
            index == 0 ? order : formatSupplyProduct(order, arr[index - 1])
          ),
    },
    page,
    tabPos,
    maxPage: Math.max(1, Math.ceil(filteredOrders.length / ORDER_TABLE_ROW_COUNT_PER_PAGE)),
    errorMessages,
    addProductDialogs,
    changeShippingDate,
    changeShippingDateUndecided,
    changeCustomerSelect,
    changeShopSelect,
    changePriceUndecided,
    submitOrders,
    changePage,
    changeTab,
    addProduct
  };
}

export default useSimpleOrderEntry;



const formatSupplyProduct = (order, lastOrder) => {
  if (!lastOrder) {
    return order;
  }

  let formattedProduct = {...order};

  for (const prop of ["productName", "originName"]) {
    if (order[prop] !== lastOrder[prop]) {
      return formattedProduct;
    }

    formattedProduct[prop] = "";
  }

  return formattedProduct;

}
